import { c as _c } from "react/compiler-runtime";
import { readFragment } from "@opensea/graphql";
import { FlexCenter, NumberDisplay, Separator, SpaceBetween, TextBody, Tooltip } from "@opensea/ui-kit";
import { Info } from "@opensea/ui-kit/icons";
import { useTranslations } from "use-intl";
import { CreatorEarningsToggle } from "../CreateOrdersSummary/CreatorEarningsToggle";
import { CreatorEarningsTooltip } from "../CreateOrdersSummary/CreatorEarningsTooltip";
import { getFloorPriceDifference, OrdersSummary, OrdersSummaryItem, OrdersSummaryItemContent, OrdersSummaryItemDescription, OrdersSummaryItemSide, OrdersSummaryItemTitle, OrdersSummaryTokenValue, OrdersSummaryTokenValueSkeleton } from "../OrdersSummary";
import { AcceptOffersSummaryFragment } from "./AcceptOffersSummary.fragment";
import { analyzeCreatorFeeStates, calculateTotalQuantity, categorizeCollectionFees, groupItemQuotesByCollection } from "./utils";
// Helper component for optional creator fees toggle
function CreatorEarningsToggleSection(t0) {
  const $ = _c(10);
  const {
    hasOptionalCreatorFees,
    applyCreatorFee,
    onToggle,
    t
  } = t0;
  if (!hasOptionalCreatorFees) {
    return null;
  }
  let t1;
  if ($[0] !== t) {
    t1 = t("creatorEarnings");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <TextBody weight="semibold">{t1}</TextBody>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== applyCreatorFee || $[5] !== onToggle) {
    t3 = <CreatorEarningsToggle checked={applyCreatorFee} creatorFeeEnforced={false} onCheckedChange={onToggle} />;
    $[4] = applyCreatorFee;
    $[5] = onToggle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <OrdersSummaryItem><OrdersSummaryItemContent><SpaceBetween className="w-full">{t2}{t3}</SpaceBetween></OrdersSummaryItemContent></OrdersSummaryItem>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// Helper component for optional creator fees breakdown
function OptionalCreatorFeesSection(t0) {
  const $ = _c(7);
  const {
    optionalCreatorFees,
    applyCreatorFee,
    total
  } = t0;
  if (!applyCreatorFee) {
    return null;
  }
  let t1;
  if ($[0] !== optionalCreatorFees || $[1] !== total) {
    let t2;
    if ($[3] !== total) {
      t2 = (collectionFee, idx) => <OrdersSummaryItem key={`optional-${idx}`}><OrdersSummaryItemContent><OrdersSummaryItemDescription className="shrink-0">{collectionFee.collectionName} (<NumberDisplay display="percent" value={collectionFee.feeBasisPoints / 10000} />)</OrdersSummaryItemDescription></OrdersSummaryItemContent><OrdersSummaryItemSide><OrdersSummaryTokenValue isFee={true} symbol={total.symbol} usdValue={collectionFee.feeUsd} value={collectionFee.fee} /></OrdersSummaryItemSide></OrdersSummaryItem>;
      $[3] = total;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = optionalCreatorFees.map(t2);
    $[0] = optionalCreatorFees;
    $[1] = total;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// Helper component for required creator fees breakdown
function RequiredCreatorFeesSection(t0) {
  const $ = _c(7);
  const {
    requiredCreatorFees,
    total
  } = t0;
  let t1;
  if ($[0] !== requiredCreatorFees || $[1] !== total) {
    let t2;
    if ($[3] !== total) {
      t2 = (collectionFee, idx) => <OrdersSummaryItem key={`required-${idx}`}><OrdersSummaryItemContent><FlexCenter className="gap-1.5"><OrdersSummaryItemDescription className="shrink-0">{collectionFee.collectionName} (<NumberDisplay display="percent" value={collectionFee.feeBasisPoints / 10000} />)</OrdersSummaryItemDescription><CreatorEarningsTooltip creatorFeeEnforced={true} forAcceptOffersSummary={true} /></FlexCenter></OrdersSummaryItemContent><OrdersSummaryItemSide><OrdersSummaryTokenValue isFee={true} symbol={total.symbol} usdValue={collectionFee.feeUsd} value={collectionFee.fee} /></OrdersSummaryItemSide></OrdersSummaryItem>;
      $[3] = total;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = requiredCreatorFees.map(t2);
    $[0] = requiredCreatorFees;
    $[1] = total;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function AcceptOffersSummary(t0) {
  const $ = _c(7);
  const {
    quote: quoteKey,
    applyCreatorFee,
    onCreatorFeeChange
  } = t0;
  const t = useTranslations("wallet.AcceptOffersSummary");
  let t1;
  if ($[0] !== applyCreatorFee || $[1] !== onCreatorFeeChange || $[2] !== quoteKey || $[3] !== t) {
    const quote = readFragment(AcceptOffersSummaryFragment, quoteKey);
    const pricePerToken = quote.totalPrice.pricePerToken;
    const fees = quote.feeSummary;
    const collections = groupItemQuotesByCollection(quote.itemQuotes);
    const {
      hasOptionalCreatorFees
    } = analyzeCreatorFeeStates(collections);
    let t2;
    if ($[5] !== onCreatorFeeChange) {
      t2 = enabled => {
        onCreatorFeeChange?.(enabled);
      };
      $[5] = onCreatorFeeChange;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    const handleCreatorFeeToggle = t2;
    const firstItemQuote = quote.itemQuotes[0];
    const floorPrice = quote.itemQuotes.length === 1 && firstItemQuote?.__typename === "ItemQuote" ? firstItemQuote.item.collection?.floorPrice?.pricePerItem.usd : undefined;
    const floorPriceDifference = getFloorPriceDifference(floorPrice, pricePerToken[0]?.usd, firstItemQuote?.__typename === "ItemQuote" ? firstItemQuote.quantity : 1);
    t1 = pricePerToken.map((t3, index) => {
      const {
        token: total,
        usd
      } = t3;
      const shouldApplyCreatorFee = applyCreatorFee ?? false;
      const totalQuantity = calculateTotalQuantity(quote.itemQuotes);
      const collectionFees = categorizeCollectionFees(collections, total, usd, totalQuantity, shouldApplyCreatorFee);
      const marketplaceFee = total.unit * (fees.marketplaceFeeBasisPoints / 10000);
      const marketplaceFeeUsd = usd * (fees.marketplaceFeeBasisPoints / 10000);
      const totalCreatorFee = collectionFees.totalOptionalFee + collectionFees.totalRequiredFee;
      const totalCreatorFeeUsd = collectionFees.totalOptionalFeeUsd + collectionFees.totalRequiredFeeUsd;
      const totalWithoutFees = total.unit - totalCreatorFee - marketplaceFee;
      const openseaFeePercentage = fees.marketplaceFeeBasisPoints / 10000;
      return <OrdersSummary key={index}>{floorPriceDifference && <OrdersSummaryItem><OrdersSummaryItemContent><OrdersSummaryItemDescription>{t("floorDifference")}</OrdersSummaryItemDescription></OrdersSummaryItemContent><OrdersSummaryItemSide><OrdersSummaryItemDescription className="font-mono"><NumberDisplay display="percent" value={floorPriceDifference} />{" "}{floorPriceDifference.gt(0) ? t("aboveFloor") : t("belowFloor")}</OrdersSummaryItemDescription></OrdersSummaryItemSide></OrdersSummaryItem>}<OrdersSummaryItem><OrdersSummaryItemContent><FlexCenter className="gap-1.5"><OrdersSummaryItemDescription>{t("platformFees")}{" "}{!Number.isNaN(openseaFeePercentage) && <>(<NumberDisplay display="percent" value={openseaFeePercentage} />)</>}</OrdersSummaryItemDescription><Tooltip content={<TextBody>{t("platformFeesTooltip")}</TextBody>} side="bottom"><Info fill="text-secondary" size={16} /></Tooltip></FlexCenter></OrdersSummaryItemContent><OrdersSummaryItemSide><OrdersSummaryTokenValue isFee={true} symbol={total.symbol} usdValue={marketplaceFeeUsd} value={marketplaceFee} /></OrdersSummaryItemSide></OrdersSummaryItem><CreatorEarningsToggleSection applyCreatorFee={applyCreatorFee ?? false} hasOptionalCreatorFees={hasOptionalCreatorFees} onToggle={handleCreatorFeeToggle} t={t} /><OptionalCreatorFeesSection applyCreatorFee={applyCreatorFee ?? false} optionalCreatorFees={collectionFees.optionalCreatorFees} total={total} /><RequiredCreatorFeesSection requiredCreatorFees={collectionFees.requiredCreatorFees} total={total} /><Separator /><OrdersSummaryItem><OrdersSummaryItemContent><OrdersSummaryItemTitle>{t("totalEstProceeds")}</OrdersSummaryItemTitle></OrdersSummaryItemContent><OrdersSummaryItemSide><OrdersSummaryTokenValue symbol={total.symbol} usdValue={usd - totalCreatorFeeUsd - marketplaceFeeUsd} value={totalWithoutFees} variant="result" /></OrdersSummaryItemSide></OrdersSummaryItem></OrdersSummary>;
    });
    $[0] = applyCreatorFee;
    $[1] = onCreatorFeeChange;
    $[2] = quoteKey;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export function AcceptOffersSummarySkeleton() {
  const $ = _c(26);
  const t = useTranslations("wallet.AcceptOffersSummary");
  let t0;
  if ($[0] !== t) {
    t0 = t("floorDifference");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <OrdersSummaryItemContent><OrdersSummaryItemDescription>{t0}</OrdersSummaryItemDescription></OrdersSummaryItemContent>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <OrdersSummaryItemSide><OrdersSummaryTokenValueSkeleton /></OrdersSummaryItemSide>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = <OrdersSummaryItem>{t1}{t2}</OrdersSummaryItem>;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("platformFees");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <OrdersSummaryItemContent><OrdersSummaryItemDescription>{t4}</OrdersSummaryItemDescription></OrdersSummaryItemContent>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <OrdersSummaryItemSide><OrdersSummaryTokenValueSkeleton /></OrdersSummaryItemSide>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5) {
    t7 = <OrdersSummaryItem>{t5}{t6}</OrdersSummaryItem>;
    $[12] = t5;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Separator />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("totalEstProceeds");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <OrdersSummaryItemContent><OrdersSummaryItemTitle>{t9}</OrdersSummaryItemTitle></OrdersSummaryItemContent>;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <OrdersSummaryItemSide><OrdersSummaryTokenValueSkeleton variant="result" /></OrdersSummaryItemSide>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10) {
    t12 = <OrdersSummaryItem>{t10}{t11}</OrdersSummaryItem>;
    $[20] = t10;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t3 || $[24] !== t7) {
    t13 = <OrdersSummary>{t3}{t7}{t8}{t12}</OrdersSummary>;
    $[22] = t12;
    $[23] = t3;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
}